import {
  DEFAULT_INTERVIEW_LANGUAGE,
  INTERVIEW_LANGUAGES,
  INTERVIEW_LANGUAGE_CODES,
  isInterviewLanguageCode,
  type InterviewLanguageCode,
} from "@talenthop/shared";

/**
 * The pure decision layer under every recruiter-facing language control (WS4, Sep 2026): the
 * New Request picker, the requisition page's Languages editor, the header pills, and the badge on
 * a report. Kept apart from React so `interviewLanguages.test.ts` can pin the rules that matter:
 *
 *   - English is never dropped. Every requisition offers English, because the notice a candidate
 *     consents to is shown in English beside any translation and the judge's citations stay in
 *     the original script. A picker that could deselect English would be able to produce a
 *     config the API rewrites to ["en"] anyway — better to show the truth up front.
 *   - "English only" is spelled as ABSENCE on the wire (`config.interviewLanguages` omitted), the
 *     same way natural interview mode rides as absence — nothing changes for the thousands of
 *     requisitions that predate this field.
 *   - A code we don't know is shown verbatim, never invented into a label, and a code we don't
 *     know is dropped from a selection before it is sent — an older draft or a newer API must
 *     never make the picker tick a box the recruiter cannot see.
 */

/** One row of GET /interview-languages — what the API says an org may offer today. */
export interface OfferedLanguage {
  code: string;
  label: string;
  nativeLabel: string;
}

/** The launch matrix as offered rows, in canonical order — the offline fallback for a picker
 *  whose fetch failed AND the source of labels for codes the API did not list. */
export const BUILT_IN_LANGUAGES: readonly OfferedLanguage[] = INTERVIEW_LANGUAGE_CODES.map((code) => ({
  code,
  label: INTERVIEW_LANGUAGES[code].label,
  nativeLabel: INTERVIEW_LANGUAGES[code].nativeLabel,
}));

/** English-only, spelled the way the picker holds it (never empty). */
export const ENGLISH_ONLY: readonly string[] = [DEFAULT_INTERVIEW_LANGUAGE];

function rank(code: string): number {
  const i = (INTERVIEW_LANGUAGE_CODES as readonly string[]).indexOf(code);
  return i === -1 ? INTERVIEW_LANGUAGE_CODES.length : i;
}

/** Human label for a code; the code itself when it is not one we know (no fabricated names). */
export function languageLabel(code: string | null | undefined): string {
  if (!code) return INTERVIEW_LANGUAGES[DEFAULT_INTERVIEW_LANGUAGE].label;
  return isInterviewLanguageCode(code) ? INTERVIEW_LANGUAGES[code].label : code;
}

/** "Tamil (தமிழ்)" — label plus native script when we know it; "xx" verbatim when we don't. */
export function languageFullLabel(code: string | null | undefined): string {
  if (!code) return languageLabel(code);
  if (!isInterviewLanguageCode(code)) return code;
  const l = INTERVIEW_LANGUAGES[code];
  return l.nativeLabel === l.label ? l.label : `${l.label} (${l.nativeLabel})`;
}

/**
 * Clean a selection from anywhere untrusted (a sessionStorage draft, an older config, a payload
 * from a newer API): known codes only, deduplicated, canonical order, English always present.
 */
export function normaliseLanguages(raw: unknown): InterviewLanguageCode[] {
  const seen = new Set<InterviewLanguageCode>([DEFAULT_INTERVIEW_LANGUAGE]);
  if (Array.isArray(raw)) for (const v of raw) if (isInterviewLanguageCode(v)) seen.add(v);
  return [...seen].sort((a, b) => rank(a) - rank(b));
}

/** Tick or untick one language. English is locked on; anything else toggles. */
export function toggleLanguage(selected: readonly string[], code: string): InterviewLanguageCode[] {
  if (code === DEFAULT_INTERVIEW_LANGUAGE) return normaliseLanguages(selected);
  const on = selected.includes(code);
  return normaliseLanguages(on ? selected.filter((c) => c !== code) : [...selected, code]);
}

/**
 * What goes on the wire as `config.interviewLanguages`: undefined when the selection is English
 * only (absence IS the default), the normalised array otherwise.
 */
export function configInterviewLanguages(selected: readonly string[]): InterviewLanguageCode[] | undefined {
  const norm = normaliseLanguages(selected);
  return norm.length <= 1 ? undefined : norm;
}

/** Same set regardless of order — decides whether a Save button has anything to save. */
export function sameLanguages(a: readonly string[], b: readonly string[]): boolean {
  const na = normaliseLanguages(a);
  const nb = normaliseLanguages(b);
  return na.length === nb.length && na.every((c, i) => c === nb[i]);
}

/** "English", "English and Tamil", "English, Tamil and Hindi" — for summaries and titles. */
export function describeLanguages(selected: readonly string[]): string {
  const labels = normaliseLanguages(selected).map(languageLabel);
  if (labels.length <= 1) return labels[0] ?? languageLabel(DEFAULT_INTERVIEW_LANGUAGE);
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

/**
 * The badge on a report's session line. null for English or an absent field — an older payload
 * (or a pre-migration API) carries no language, and "interviewed in English" is what every
 * interview before this feature was, so the only honest rendering of "no claim" is no badge.
 */
export function languageBadge(language: string | null | undefined): { label: string; title: string } | null {
  if (!language || language === DEFAULT_INTERVIEW_LANGUAGE) return null;
  const full = languageFullLabel(language);
  return {
    label: `Interviewed in ${full}`,
    title: `The candidate chose ${full} at consent. Asha spoke and listened in that language; the answers below are the original transcript, with the assessment model's English rendering beneath each where it produced one.`,
  };
}

/**
 * Merge the API's offered rows with what a requisition already lists: a language the org can no
 * longer offer (an owner turned it off after this request was created) still has to be visible,
 * ticked and un-tickable — hiding it would silently drop it from the next save. Offered rows lead
 * in the API's order; leftovers follow in canonical order, flagged so the UI can say why.
 */
export function pickerRows(
  offered: readonly OfferedLanguage[],
  selected: readonly string[],
): { code: string; label: string; nativeLabel: string; offered: boolean }[] {
  const rows = offered.map((o) => ({ ...o, offered: true }));
  const have = new Set(rows.map((r) => r.code));
  for (const code of normaliseLanguages(selected)) {
    if (have.has(code)) continue;
    const known = BUILT_IN_LANGUAGES.find((b) => b.code === code);
    rows.push({ code, label: known?.label ?? code, nativeLabel: known?.nativeLabel ?? code, offered: false });
  }
  return rows;
}
